import {
  AthleteTeamProfile,
  athleteDisplayName,
  MetricConfig,
} from '@strength/domain';
import { parseExactOrRange } from '@strength/domain-workout';
import { DateTime } from 'luxon';

/**
 * A KPI grid column: the metric mapped to the grid. Mirrors the shape returned
 * by `trpc.benchmarks.kpiGridMetrics` (ordered by `sortOrder`).
 */
export type KpiGridMetric = {
  id: string;
  name: string;
  type: string;
  isCollectable: boolean;
  sortOrder: number;
  config: MetricConfig;
};

/**
 * A recorded KPI value row. Mirrors `trpc.athleteKpiBenchmarks.teamKpiBenchmarks`
 * (one row per (profileId, metricId): the latest by date).
 */
export type TeamKpiBenchmark = {
  id: string;
  profileId: string;
  benchmarkGridId: string;
  metricId: string;
  value: number;
  unit: string;
  date: string;
};

/** The current value for a single (athlete, metric) cell. */
export type KpiBenchmarkCellValue = {
  value: number | null;
  unit: string | null;
};

/**
 * A grid row: one athlete plus a cell per metric keyed by metric id. Matches the
 * rep grid's `AthleteExerciseBenchmarkRow` intersection pattern.
 */
export type AthleteKpiBenchmarkRow = {
  id: string;
  index: number;
  profile: string;
  athlete: AthleteTeamProfile;
} & { [metricId: string]: KpiBenchmarkCellValue };

/** Payload handed to the record callback for a single cell entry. */
export type RecordAthleteKpiBenchmarkCell = {
  profileId: string;
  metricId: string;
  benchmarkGridId: string;
  value: number;
  unit: string;
};

/**
 * The `trpc.athleteKpiBenchmarks.recordAthleteKpiBenchmark` input: the cell
 * payload plus the recorded date. `date` is any value Luxon can parse
 * (`DateTime.now().toISO()` full offset timestamp, or a `yyyy-MM-dd` date).
 */
export type RecordAthleteKpiBenchmarkInput = RecordAthleteKpiBenchmarkCell & {
  date: string;
};

/**
 * Builds the record mutation input from a recorded cell and the record time.
 * Kept pure (no clock access) so the caller supplies `DateTime.now().toISO()`
 * and this stays unit-testable.
 */
export function buildRecordKpiBenchmarkInput(
  cell: RecordAthleteKpiBenchmarkCell,
  date: string,
): RecordAthleteKpiBenchmarkInput {
  return {
    profileId: cell.profileId,
    metricId: cell.metricId,
    benchmarkGridId: cell.benchmarkGridId,
    value: cell.value,
    unit: cell.unit,
    date,
  };
}

/**
 * Whether the benchmarks page should render the KPI grid (and KPI history) for
 * the selected grid: only for `kpi`-type grids. Rep grids render the
 * exercise-benchmark path unchanged.
 */
export function shouldRenderKpiGrid(
  grid: { type: string } | undefined,
): boolean {
  return grid?.type === 'kpi';
}

export type KpiEntryValidation = { ok: true; value: number } | { ok: false };

/** View-mode display of a cell: `52 ml/kg/min`, `12`, or `` when empty. */
export function formatKpiCellDisplay(
  cell: KpiBenchmarkCellValue | undefined,
): string {
  if (!cell || cell.value === null || cell.value === undefined) {
    return '';
  }
  return cell.unit ? `${cell.value} ${cell.unit}` : `${cell.value}`;
}

/** Bare numeric value for the edit input (no unit), or `` when empty. */
export function kpiCellInputValue(
  cell: KpiBenchmarkCellValue | undefined,
): string {
  if (!cell || cell.value === null || cell.value === undefined) {
    return '';
  }
  return `${cell.value}`;
}

/**
 * Unit to record for a new entry: reuse the cell's existing unit if present,
 * otherwise the metric's first configured unit (empty when the metric has none).
 */
export function resolveKpiUnit(
  cell: KpiBenchmarkCellValue | undefined,
  metric: KpiGridMetric,
): string {
  return cell?.unit ?? metric.config.units[0] ?? '';
}

/**
 * Numeric-only validation for a KPI cell entry. Rejects keyword / non-numeric
 * input, unit suffixes, ranges, empty values and negatives. Honors the metric's
 * `numericType` (integers reject decimals). Reuses the shared metric parser so
 * client validation matches the workout metric rules.
 */
export function validateKpiEntry(
  input: string,
  config: MetricConfig,
): KpiEntryValidation {
  const trimmed = input.trim();
  if (trimmed === '') {
    return { ok: false };
  }
  const numericType = config.numericType === 'integer' ? 'integer' : 'decimal';
  const result = parseExactOrRange(trimmed, numericType, false);
  if (!result) {
    return { ok: false };
  }
  return { ok: true, value: result.min };
}

/**
 * Builds grid rows (metric columns x athlete rows) from the team's athletes, the
 * grid's metrics and the latest-per-(athlete,metric) benchmark values. Missing
 * (athlete, metric) pairs become empty cells. Benchmarks for metrics not on the
 * grid are ignored.
 */
export function buildAthleteKpiBenchmarkRows(
  athletes: AthleteTeamProfile[],
  metrics: KpiGridMetric[],
  benchmarks: TeamKpiBenchmark[],
): AthleteKpiBenchmarkRow[] {
  const metricIds = new Set(metrics.map((metric) => metric.id));

  return athletes.map((athlete, index) => {
    const benchmarksForAthlete = benchmarks.filter(
      (benchmark) => benchmark.profileId === athlete.id,
    );

    const emptyCells = Object.assign(
      {},
      ...metrics.map((metric) => ({
        [metric.id]: { value: null, unit: null } as KpiBenchmarkCellValue,
      })),
    );

    const cells = Object.assign(
      emptyCells,
      ...benchmarksForAthlete.flatMap((benchmark) => {
        if (!metricIds.has(benchmark.metricId)) {
          return [];
        }
        return [
          {
            [benchmark.metricId]: {
              value: benchmark.value,
              unit: benchmark.unit,
            } as KpiBenchmarkCellValue,
          },
        ];
      }),
    );

    return {
      id: athlete.id,
      index,
      profile: athleteDisplayName(athlete),
      athlete,
      ...cells,
    };
  });
}

/**
 * A single recorded KPI value row. Mirrors the shape returned by
 * `trpc.athleteKpiBenchmarks.fetchKpiBenchmarkHistory` (one row per recorded
 * entry, ordered by `date` ascending / oldest-first).
 */
export type KpiBenchmarkHistoryEntry = {
  id: string;
  profileId: string;
  benchmarkGridId: string;
  metricId: string;
  value: number;
  unit: string;
  date: string;
  workoutId: string | null;
  workoutSetId: string | null;
};

/**
 * The drawer's derived view over an athlete/metric's recorded history:
 * entries ordered newest-first, the current (latest) entry, and whether the
 * history is empty.
 */
export type KpiBenchmarkHistoryView = {
  entries: KpiBenchmarkHistoryEntry[];
  current: KpiBenchmarkHistoryEntry | null;
  isEmpty: boolean;
};

/**
 * Derives the history drawer view from the router's ascending (oldest-first)
 * rows: reorders newest-first, picks the current (latest) value and flags the
 * empty state. Sorts by `date` descending (robust to unsorted input); ties on
 * equal dates are broken by original order so the most-recently-recorded entry
 * (later in the ascending input) is treated as current. Does not mutate input.
 */
export function buildKpiBenchmarkHistoryView(
  entries: KpiBenchmarkHistoryEntry[],
): KpiBenchmarkHistoryView {
  const newestFirst = entries
    // Precompute each row's epoch millis once (native Date, not Luxon) so the
    // comparator does no per-comparison date parsing on this hot path.
    .map((entry, index) => ({
      entry,
      index,
      millis: new Date(entry.date).getTime(),
    }))
    .sort((a, b) => {
      const byDate = b.millis - a.millis;
      return byDate !== 0 ? byDate : b.index - a.index;
    })
    .map(({ entry }) => entry);

  return {
    entries: newestFirst,
    current: newestFirst[0] ?? null,
    isEmpty: newestFirst.length === 0,
  };
}

/** Value + unit display for a history entry (e.g. `52 ml/kg/min`, `12`). */
export function formatKpiHistoryValue(entry: KpiBenchmarkHistoryEntry): string {
  return formatKpiCellDisplay({ value: entry.value, unit: entry.unit });
}

/** Short, locale-aware date for a history entry's recorded date. */
export function formatKpiHistoryDate(date: string, locale: string): string {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT, { locale });
}
